import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useReportFilters } from '~/context/ReportFiltersProvider';
import { FaEye, FaSort, FaSortUp, FaSortDown, FaSync, FaCog, FaTimes, FaSave } from 'react-icons/fa';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { Pagination } from '~/components/Pagination';
import { searchCampaignsAdvanced, fetchCampaignMetadata } from '~/services/campaign';
import SaveSearchModal from '~/components/Reports/SaveSearchModal';
import { ALL_COLUMNS, COLUMNS_BY_GROUP, parseVisibleColumns } from './campaignColumns';

// Filter labels for display
const FILTER_LABELS: Record<string, string> = {
    ntaf_id: 'NTAF ID',
    campaign_status: 'Status',
    legacy_status: 'Legacy Status',
    privacy: 'Privacy',
    living_donor: 'Living Donor',
    deceased: 'Deceased',
    state: 'State',
    region: 'Region',
    zip_code: 'Zip Code',
    city: 'City',
    medical_reason: 'Condition',
    medical_issue: 'Diagnosis',
    referred_by: 'Referred By',
    csc: 'CSC',
    special_circumstance: 'Special Circumstance',
    campaign_created_start_date: 'Created From',
    campaign_created_end_date: 'Created To',
    campaign_funded_start_date: 'Open From',
    campaign_funded_end_date: 'Open To',
    funds_raised_start_date: 'Raised From',
    funds_raised_end_date: 'Raised To',
    funds_raised_min: 'Min Raised',
    funds_raised_max: 'Max Raised',
    unique_donors_min: 'Min Donors',
    unique_donors_max: 'Max Donors',
    ethnicity: 'Ethnicity',
    dob_start_date: 'Born From',
    dob_end_date: 'Born To',
    age_min: 'Min Age',
    age_max: 'Max Age',
};

interface SearchResult {
    campaign_id: number;
    ntaf_id: string;
    fund_name: string;
    campaign_status: {
        name: string;
        code: string;
    };
    campaign_status_date: string;
    privacy_status: string;
    fundraising_goal: number;
    funds_raised: number;
    funded_date: string;
    region: {
        code: string;
        name: string;
    } | null;
    state: {
        code: string;
        name: string;
    } | null;
    zip_code: string;
    city?: string | null;
    client_services_coordinator: {
        id: number;
        name: string;
    } | null;
    csc_email?: string | null;
    opted_in_for_find_a_friend: boolean;
    special_circumstance: string;
    living_donor: boolean | null;
    medical_reason?: string;
    primary_medical_issue: string;
    created_date: string;
    update_date: string;
    date_of_birth?: string | null;
    date_of_death?: string | null;
    deceased?: boolean | null;
    age?: number | null;
    ethnicity?: string | null;
    household_size?: string | null;
    single_parent_household?: string | null;
    household_income?: string;
    address?: string;
    insurance?: string;
    photo_consented?: boolean | null;
    donor_count?: number | null;
    primary_contact_email?: string;
    client_email?: string;
    initial_contact_email?: string;
    medical_professional?: string;
    medical_center?: string;
    medical_center_location?: string;
    referred_by?: string;
    calculated_fund_name?: string | null;
    first_name?: string | null;
    middle_name?: string | null;
    last_name?: string | null;
}

const CampaignSearchResults: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();
    const [results, setResults] = useState<SearchResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [refreshKey, setRefreshKey] = useState(0);
    const [showColumnConfig, setShowColumnConfig] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<string[]>(
        () => parseVisibleColumns(searchParams.get('visible_columns')),
    );
    const columnConfigRef = useRef<HTMLDivElement>(null);
    const [cscNameMap, setCscNameMap] = useState<Record<string, string>>({});
    // Condition, diagnosis and referral source travel in the URL as ids, so
    // the filter chips need their names to show anything readable.
    const [medicalNameMap, setMedicalNameMap] = useState<Record<string, string>>({});

    useEffect(() => {
        const loadMetadataNames = async () => {
            try {
                const metadata = await fetchCampaignMetadata();
                if (metadata?.patient_services_coordinators) {
                    const map: Record<string, string> = {};
                    metadata.patient_services_coordinators.forEach((psc: { id: number; full_name: string }) => {
                        map[String(psc.id)] = psc.full_name;
                    });
                    setCscNameMap(map);
                }
                const medicalMap: Record<string, string> = {};
                (metadata?.medical_reasons || []).forEach((reason: { id: number; name: string }) => {
                    medicalMap[`medical_reason:${reason.id}`] = reason.name;
                });
                (metadata?.medical_issues || []).forEach((issue: { id: number; name: string }) => {
                    medicalMap[`medical_issue:${issue.id}`] = issue.name;
                });
                (metadata?.heard_of_sources || []).forEach((source: { id: number; name: string }) => {
                    medicalMap[`referred_by:${source.id}`] = source.name;
                });
                setMedicalNameMap(medicalMap);
            } catch (error) {
                console.error('Error fetching filter names:', error);
            }
        };
        if (
            searchParams.has('csc')
            || searchParams.has('medical_reason')
            || searchParams.has('medical_issue')
            || searchParams.has('referred_by')
        ) {
            loadMetadataNames();
        }
    }, [searchParams]);

    const handleRefresh = useCallback(() => {
        setRefreshKey(prev => prev + 1);
    }, []);

    // Close column config dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (columnConfigRef.current && !columnConfigRef.current.contains(event.target as Node)) {
                setShowColumnConfig(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const toggleColumn = (columnKey: string) => {
        setVisibleColumns(prev => 
            prev.includes(columnKey) 
                ? prev.filter(k => k !== columnKey)
                : [...prev, columnKey]
        );
    };

    // Get columns that are currently visible
    const getVisibleColumns = () => {
        return ALL_COLUMNS.filter(col => visibleColumns.includes(col.key));
    };

    // Build the JSON payload for saving the current search
    const buildSaveJsonFields = () => {
        const filters: Record<string, string | string[]> = {};
        searchParams.forEach((value, key) => {
            if (key === 'page' || key === 'ordering' || key === 'visible_columns') return;
            const existing = filters[key];
            if (existing === undefined) {
                filters[key] = value;
            } else if (Array.isArray(existing)) {
                existing.push(value);
            } else {
                filters[key] = [existing, value];
            }
        });
        return JSON.stringify({
            report_type_select: 'campaign_search',
            filters,
            visible_columns: visibleColumns,
        });
    };

    // Get active filters from search params
    const getActiveFilters = () => {
        const filters: { key: string; label: string; value: string; displayValue: string }[] = [];
        const excludeKeys = ['page', 'ordering', 'visible_columns'];

        searchParams.forEach((value, key) => {
            if (excludeKeys.includes(key) || !value) return;

            const label = FILTER_LABELS[key] || key;
            let displayValue = value;

            // Format specific values for better display
            if (key === 'csc') {
                displayValue = cscNameMap[value] || value;
            } else if (key === 'medical_reason' || key === 'medical_issue' || key === 'referred_by') {
                displayValue = medicalNameMap[`${key}:${value}`] || value;
            } else if (key === 'privacy') {
                displayValue = value.charAt(0).toUpperCase() + value.slice(1);
            } else if (key === 'living_donor' || key === 'deceased') {
                displayValue = value === 'true' ? 'Yes' : 'No';
            } else if (key.includes('date')) {
                displayValue = new Date(value + 'T00:00:00').toLocaleDateString();
            } else if (key === 'funds_raised_min' || key === 'funds_raised_max') {
                displayValue = `$${Number(value).toLocaleString()}`;
            }

            filters.push({ key, label, value, displayValue });
        });

        return filters;
    };

    // Remove a filter
    const removeFilter = (key: string, value?: string) => {
        const params = new URLSearchParams(searchParams);
        if (value) {
            // For multi-value params, only remove the specific value
            const values = params.getAll(key).filter(v => v !== value);
            params.delete(key);
            values.forEach(v => params.append(key, v));
        } else {
            params.delete(key);
        }
        params.set('page', '1'); // Reset to first page
        setSearchParams(params);
    };

    // Clear all filters
    const { clearFilterParams } = useReportFilters();
    const clearAllFilters = () => {
        clearFilterParams();
        navigate('/new-report');
    };

    // Function to render cell content based on column type
    const renderCellContent = (campaign: SearchResult, columnKey: string) => {
        switch (columnKey) {
            case 'campaign_id':
                return campaign.campaign_id;
            case 'ntaf_id':
                return campaign.ntaf_id;
            case 'fund_name':
                return campaign.fund_name;
            case 'status':
                return campaign.campaign_status?.name || '';
            case 'privacy':
                return campaign.privacy_status || '';
            case 'goal':
                return campaign.fundraising_goal ? `$${campaign.fundraising_goal.toLocaleString()}` : '';
            case 'raised':
                return campaign.funds_raised ? `$${campaign.funds_raised.toLocaleString()}` : '$0';
            case 'donors':
                return campaign.donor_count ?? '';
            case 'state':
                return campaign.state?.name || '';
            case 'medical_reason':
                return campaign.medical_reason || '';
            case 'medical_issue':
                return campaign.primary_medical_issue || '';
            case 'region':
                return campaign.region?.name || '';
            case 'zip_code':
                return campaign.zip_code || '';
            case 'city':
                return campaign.city || '';
            case 'csc':
                return campaign.client_services_coordinator?.name || '';
            case 'csc_email':
                return campaign.csc_email || '';
            case 'created_date':
                return campaign.created_date ? new Date(campaign.created_date + 'T00:00:00').toLocaleDateString() : '';
            case 'funded_date':
                return campaign.funded_date ? new Date(campaign.funded_date + 'T00:00:00').toLocaleDateString() : '';
            case 'date_of_birth':
                // Parse as local midnight so the date never shifts a day back
                // in negative-offset timezones, matching created_date above.
                return campaign.date_of_birth
                    ? new Date(campaign.date_of_birth + 'T00:00:00').toLocaleDateString()
                    : '';
            case 'age':
                // 0 is a real age (infant clients), so test for null/undefined
                return campaign.age === null || campaign.age === undefined
                    ? ''
                    : String(campaign.age);
            case 'deceased':
                // Derived server-side from the deceased flag or a recorded
                // date of death (HHLTB-2778); null = no client on the campaign
                if (campaign.deceased === null || campaign.deceased === undefined) return '';
                return campaign.deceased ? 'Yes' : 'No';
            case 'date_of_death':
                return campaign.date_of_death
                    ? new Date(campaign.date_of_death + 'T00:00:00').toLocaleDateString()
                    : '';
            case 'ethnicity':
                return campaign.ethnicity || '';
            case 'household_size':
                return campaign.household_size || '';
            case 'single_parent_household':
                return campaign.single_parent_household || '';
            case 'household_income':
                return campaign.household_income ? `$${Number(campaign.household_income).toLocaleString()}` : '';
            case 'address':
                return campaign.address || '';
            case 'insurance':
                return campaign.insurance || '';
            case 'photo_consented':
                // null/undefined = no application (e.g. non-patient campaign) → blank
                if (campaign.photo_consented === null || campaign.photo_consented === undefined) return '';
                return campaign.photo_consented ? 'Yes' : 'No';
            case 'primary_contact_email':
                return campaign.primary_contact_email || '';
            case 'client_email':
                return campaign.client_email || '';
            case 'initial_contact_email':
                return campaign.initial_contact_email || '';
            case 'medical_professional':
                return campaign.medical_professional || '';
            case 'medical_center':
                return campaign.medical_center || '';
            case 'medical_center_location':
                return campaign.medical_center_location || '';
            case 'referred_by':
                return campaign.referred_by || '';
            case 'calculated_fund_name':
                return campaign.calculated_fund_name || '';
            case 'first_name':
                return campaign.first_name || '';
            case 'middle_name':
                return campaign.middle_name || '';
            case 'last_name':
                return campaign.last_name || '';
            default:
                return '';
        }
    };

    useEffect(() => {
        const performSearch = async () => {
            setLoading(true);
            setError(null);

            try {
                // Get page from URL params or default to 1
                const page = parseInt(searchParams.get('page') || '1', 10);
                setCurrentPage(page);

                // Get sorting from URL params
                const ordering = searchParams.get('ordering');
                if (ordering) {
                    if (ordering.startsWith('-')) {
                        setSortField(ordering.substring(1));
                        setSortDirection('desc');
                    } else {
                        setSortField(ordering);
                        setSortDirection('asc');
                    }
                }

                // Convert URLSearchParams to a regular object (excluding page, but including ordering)
                const filters: Record<string, any> = {};
                searchParams.forEach((value, key) => {
                    if (key === 'page' || key === 'visible_columns') return;
                    if (filters[key]) {
                        // Handle multiple values for the same key (for multi-select fields)
                        if (Array.isArray(filters[key])) {
                            filters[key].push(value);
                        } else {
                            filters[key] = [filters[key], value];
                        }
                    } else {
                        filters[key] = value;
                    }
                });

                const response = await searchCampaignsAdvanced(filters, page);
                console.log('Search response:', response); // Debug log
                setResults(response.results || response);

                // Handle pagination data from Django REST framework
                if (response.count !== undefined) {
                    setTotalCount(response.count);
                    // Calculate total pages (20 items per page as configured in backend)
                    setTotalPages(Math.ceil(response.count / 20));
                } else {
                    // Fallback: if no pagination data, assume we have all results
                    setTotalCount(Array.isArray(response.results) ? response.results.length : (Array.isArray(response) ? response.length : 0));
                    setTotalPages(1);
                }
            } catch (err) {
                setError('Failed to perform search. Please try again.');
                console.error('Search error:', err);
            } finally {
                setLoading(false);
            }
        };

        performSearch();
    }, [searchParams, refreshKey]);

    const handleBackToSearch = () => {
        // Preserve search parameters when going back (excluding page and sort)
        const params = new URLSearchParams(searchParams);
        params.delete('page');
        params.delete('ordering');
        navigate(`/new-report?${params.toString()}`);
    };

    const handlePageChange = (newPage: number) => {
        const params = new URLSearchParams(searchParams);
        params.set('page', newPage.toString());
        setSearchParams(params);
    };

    const handleSort = (field: string) => {
        const params = new URLSearchParams(searchParams);

        // If clicking the same field, toggle direction
        if (sortField === field) {
            const newDirection = sortDirection === 'asc' ? 'desc' : 'asc';
            setSortDirection(newDirection);
            params.set('ordering', newDirection === 'desc' ? `-${field}` : field);
        } else {
            // New field, default to ascending
            setSortField(field);
            setSortDirection('asc');
            params.set('ordering', field);
        }

        // Reset to first page when sorting
        params.set('page', '1');
        setSearchParams(params);
    };

    const getSortIcon = (field: string) => {
        if (sortField !== field) {
            return <FaSort className="ml-1 text-gray-400" />;
        }
        return sortDirection === 'asc' ?
            <FaSortUp className="ml-1 text-primary" /> :
            <FaSortDown className="ml-1 text-primary" />;
    };

    const handleExport = useCallback(async () => {
        if (totalCount === 0) return;

        setIsExporting(true);
        try {
            // Build filters object from search params (excluding pagination/sort)
            const filters: Record<string, any> = {};
            searchParams.forEach((value, key) => {
                if (key !== 'page' && key !== 'ordering' && key !== 'visible_columns') {
                    if (filters[key]) {
                        if (Array.isArray(filters[key])) {
                            filters[key].push(value);
                        } else {
                            filters[key] = [filters[key], value];
                        }
                    } else {
                        filters[key] = value;
                    }
                }
            });

            // Fetch all pages of results
            const allResults: SearchResult[] = [];
            const pageSize = 20;
            const totalPagesToFetch = Math.ceil(totalCount / pageSize);

            for (let page = 1; page <= totalPagesToFetch; page++) {
                const response = await searchCampaignsAdvanced(filters, page);
                const pageResults = response.results || response;
                if (Array.isArray(pageResults)) {
                    allResults.push(...pageResults);
                }
            }

            if (allResults.length === 0) return;

            // Use only the user-selected visible columns for CSV export
            const csvColumns = getVisibleColumns().map(col => ({
                key: col.key,
                label: col.label,
            }));

            // Helper to escape CSV values
            const escapeCSV = (value: string | null | undefined): string => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            // Build CSV content
            const header = csvColumns.map(col => escapeCSV(col.label)).join(',');
            const rows = allResults.map((campaign: SearchResult) => {
                return csvColumns.map(col => {
                    return escapeCSV(String(renderCellContent(campaign, col.key) ?? ''));
                }).join(',');
            });

            const csvContent = [header, ...rows].join('\n');

            // Create and trigger download
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.setAttribute('href', url);
            link.setAttribute('download', `campaign-search-${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Export error:', err);
            setError('Failed to export search results. Please try again.');
        } finally {
            setIsExporting(false);
        }
    }, [totalCount, searchParams, visibleColumns]);

    if (loading) {
        return (
            <>
                <Breadcrumb pageName='Campaign Report' parentPage='Reports' parentPath='/new-report' />
                <div className='flex justify-center items-center h-64'>
                    <div className='text-lg'>Loading search results...</div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <Breadcrumb pageName='Campaign Report' parentPage='Reports' parentPath='/new-report' />
                <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                    <div className='text-center py-8'>
                        <div className='text-red-500 text-lg mb-4'>{error}</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 font-medium'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Breadcrumb pageName='Campaign Report' parentPage='Reports' parentPath='/new-report' />

            <div className='rounded-sm border border-stroke bg-white px-5 pt-6 pb-2.5 shadow-default sm:px-7.5 xl:pb-1'>
                <div className='flex justify-between items-center mb-6'>
                    <p className='text-sm text-gray-600'>
                        {totalCount > 0 ? (
                            totalPages > 1 ?
                                `Showing ${((currentPage - 1) * 20) + 1}-${Math.min(currentPage * 20, totalCount)} of ${totalCount} results` :
                                `${totalCount} result${totalCount !== 1 ? 's' : ''} found`
                        ) : 'No results found'}
                    </p>
                    <div className='flex gap-3'>
                        {/* Column Configuration Dropdown */}
                        <div className='relative' ref={columnConfigRef}>
                            <button
                                onClick={() => setShowColumnConfig(!showColumnConfig)}
                                className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors duration-200'
                                title='Configure columns'
                            >
                                <FaCog className='mr-2' size={14} />
                                Columns
                            </button>
                            {showColumnConfig && (
                                <div className='absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50'>
                                    <div className='p-3 border-b border-gray-200'>
                                        <span className='text-sm font-semibold text-gray-700'>Show/Hide Columns</span>
                                    </div>
                                    <div className='max-h-64 overflow-y-auto p-2'>
                                        {COLUMNS_BY_GROUP.map(({ group, columns }) => (
                                            <div key={group} className='mb-2 last:mb-0'>
                                                <div className='px-2 py-1 text-[11px] font-bold uppercase tracking-wide text-gray-400'>
                                                    {group}
                                                </div>
                                                {columns.map(column => (
                                                    <label
                                                        key={column.key}
                                                        className='flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer'
                                                    >
                                                        <input
                                                            type='checkbox'
                                                            checked={visibleColumns.includes(column.key)}
                                                            onChange={() => toggleColumn(column.key)}
                                                            className='rounded border-gray-300 text-primary focus:ring-primary'
                                                        />
                                                        <span className='text-sm text-gray-700'>{column.label}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <button
                            onClick={() => setShowSaveModal(true)}
                            className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 transition-colors duration-200'
                            title='Save this search'
                        >
                            <FaSave className='mr-2' size={14} />
                            Save Search
                        </button>
                        <button
                            onClick={handleRefresh}
                            disabled={loading}
                            className='inline-flex items-center rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-4 focus:ring-gray-300 disabled:opacity-50 transition-colors duration-200'
                            title='Refresh results'
                        >
                            <FaSync className={`mr-2 ${loading ? 'animate-spin' : ''}`} size={14} />
                            Refresh
                        </button>
                        <button
                            onClick={handleExport}
                            disabled={isExporting}
                            className='inline-flex items-center rounded-lg bg-[#00adb3] px-4 py-2 text-sm font-medium text-white hover:bg-[#00adb3]/90 focus:outline-none focus:ring-4 focus:ring-[#00adb3]/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200'
                        >
                            {isExporting ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Exporting...
                                </>
                            ) : (
                                <>
                                    <svg className="mr-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    Export CSV
                                </>
                            )}
                        </button>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90 focus:outline-none focus:ring-4 focus:ring-primary/50 transition-colors duration-200'
                        >
                            Back to Search
                        </button>
                    </div>
                </div>

                {/* Active Filters Display */}
                {getActiveFilters().length > 0 && (
                    <div className='mb-4 pb-4 border-b border-gray-200'>
                        <div className='flex items-center flex-wrap gap-2'>
                            <span className='text-sm font-medium text-gray-600'>Filters:</span>
                            {getActiveFilters().map((filter, index) => (
                                <span
                                    key={`${filter.key}-${filter.value}-${index}`}
                                    className='inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#00adb3]/10 text-[#00adb3] text-sm'
                                >
                                    <span className='font-medium'>{filter.label}:</span> {filter.displayValue}
                                    <button
                                        onClick={() => removeFilter(filter.key, filter.value)}
                                        className='ml-1 hover:text-[#00adb3]/70 transition-colors'
                                        title={`Remove ${filter.label} filter`}
                                    >
                                        <FaTimes size={12} />
                                    </button>
                                </span>
                            ))}
                            <button
                                onClick={clearAllFilters}
                                className='text-sm text-gray-500 hover:text-gray-700 underline ml-2'
                            >
                                Clear all
                            </button>
                        </div>
                    </div>
                )}

                {results.length === 0 && !loading ? (
                    <div className='text-center py-8'>
                        <div className='text-lg text-gray-600 mb-4'>No campaigns found matching your criteria.</div>
                        <button
                            onClick={handleBackToSearch}
                            className='rounded-lg bg-primary px-6 py-2 text-white hover:bg-primary/90 font-medium'
                        >
                            Try Different Filters
                        </button>
                    </div>
                ) : results.length > 0 && (
                    <div className='max-w-full overflow-x-auto'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='bg-gray-2 text-left'>
                                    {/* A calculated column has nothing to order
                                        by, so it doesn't offer a sort that would
                                        silently sort by something else. */}
                                    {getVisibleColumns().map((column, index) => (
                                        <th
                                            key={column.key}
                                            className={`min-w-[120px] py-4 px-4 font-medium text-black select-none ${column.sortKey ? 'cursor-pointer hover:bg-gray-3' : ''
                                                } ${index === 0 ? 'xl:pl-11' : ''
                                                }`}
                                            onClick={() =>
                                                column.sortKey && handleSort(column.sortKey)
                                            }
                                        >
                                            <div className='flex items-center'>
                                                {column.label}
                                                {column.sortKey && getSortIcon(column.sortKey)}
                                            </div>
                                        </th>
                                    ))}
                                    <th className='py-4 px-4 font-medium text-black'>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {results.map((campaign) => (
                                    <tr key={campaign.campaign_id}>
                                        {getVisibleColumns().map((column, index) => (
                                            <td
                                                key={column.key}
                                                className={`border-b border-[#eee] py-5 px-4 ${index === 0 ? 'pl-9 xl:pl-11' : ''
                                                    }`}
                                            >
                                                <p className='text-black'>
                                                    {renderCellContent(campaign, column.key)}
                                                </p>
                                            </td>
                                        ))}
                                        <td className='border-b border-[#eee] py-5 px-4'>
                                            {/* A new tab keeps the report the staffer was working
                                                intact while they open a campaign (HHLTB-2783). */}
                                            <Link
                                                to={`/campaigns/${campaign.campaign_id}`}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className='inline-flex items-center justify-center w-8 h-8 rounded text-primary hover:text-primary/80 hover:bg-primary/10 transition-colors duration-200'
                                                title='View Campaign Details'
                                            >
                                                <FaEye size={16} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* Pagination - only show if there are results and more than one page */}
                {results.length > 0 && totalPages > 1 && (
                    <Pagination
                        page={currentPage}
                        setPage={handlePageChange}
                        pages={totalPages}
                        totalCount={totalCount}
                    />
                )}
            </div>

            <SaveSearchModal
                isOpen={showSaveModal}
                onClose={() => setShowSaveModal(false)}
                jsonFields={buildSaveJsonFields()}
            />
        </>
    );
};

export default CampaignSearchResults; 